import { io } from 'socket.io-client';
import store from '../vuex';

// WebSocket Events enum
export const WebSocketEvents = {
  CONNECTION: 'connection',
  DISCONNECT: 'disconnect',
  JOIN_ROOM: 'join-room',
  LEAVE_ROOM: 'leave-room',
  MESSAGE: 'message',
  NEW_CHAT_MESSAGES: 'new-chat-messages',
  NOTIFICATION: 'notification',
  USER_STATUS: 'user-status',
  STREAMING_UPDATE: 'streaming-update',
  ERROR: 'error',
};

class SocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.currentRoom = null;
    this.eventListeners = new Map();
  }

  connect(url = null) {
    if (this.socket && this.socket.connected) {
      return this.socket;
    }

    const socketUrl = url || process.env.SOCKET_URL;

    this.socket = io(socketUrl, {
      path: process.env.SOCKET_PATH,
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 10,
      timeout: 30000,
      transports: ['websocket', 'polling'],
      ...(store.state.jwt && {
        auth: {
          token: store.state.jwt,
        },
      }),
    });

    this.setupEventListeners();
    return this.socket;
  }

  setupEventListeners() {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      this.isConnected = true;
    });

    this.socket.on('disconnect', () => {
      this.isConnected = false;
    });

    this.socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error);
      this.isConnected = false;
    });

    this.socket.on('reconnect', (attemptNumber) => {
      console.log('WebSocket reconnected after', attemptNumber, 'attempts');
    });

    this.socket.on('reconnect_error', (error) => {
      console.error('WebSocket reconnection error:', error);
    });
  }

  joinRoom(roomId) {
    if (!this.socket || !this.isConnected) {
      console.warn('Socket not connected, cannot join room');
      return;
    }

    this.currentRoom = roomId;
    this.socket.emit(WebSocketEvents.JOIN_ROOM, { room: roomId });
  }

  leaveRoom(roomId = null) {
    if (!this.socket) return;

    const room = roomId || this.currentRoom;
    if (room) {
      this.socket.emit(WebSocketEvents.LEAVE_ROOM, { room: room });
    }

    if (!roomId) {
      this.currentRoom = null;
    }
  }

  on(event, callback) {
    if (!this.socket) {
      console.warn('Socket not initialized');
      return;
    }

    this.socket.on(event, callback);

    // Store listener for cleanup
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event).push(callback);
  }

  off(event, callback = null) {
    if (!this.socket) return;

    if (callback) {
      this.socket.off(event, callback);

      // Remove from stored listeners
      const listeners = this.eventListeners.get(event);
      if (listeners) {
        const index = listeners.indexOf(callback);
        if (index > -1) {
          listeners.splice(index, 1);
        }
      }
    } else {
      this.socket.off(event);
      this.eventListeners.delete(event);
    }
  }

  emit(event, data) {
    if (!this.socket || !this.isConnected) {
      console.warn('Socket not connected, cannot emit event:', event);
      return;
    }

    this.socket.emit(event, data);
  }

  disconnect() {
    if (this.socket) {
      // Leave current room before disconnecting
      this.leaveRoom();

      // Clean up all event listeners
      this.eventListeners.clear();

      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.currentRoom = null;
    }
  }

  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      currentRoom: this.currentRoom,
      socketId: this.socket?.id || null,
    };
  }
}

// Create singleton instance
const socketService = new SocketService();

export default socketService;
